/**
 * Shared design tokens — consumed by BOTH the PDF generator and the web dashboard.
 * Single source of truth; never hard-code a color, font, or dimension elsewhere.
 *
 * PDF and dashboard both render with Inter (Spec v2 §1).
 * Both rely on the same COLORS palette and scoring helpers, so dashboard values
 * match the PDF bit-for-bit (COHERENCE RULE).
 * @module shared/designTokens
 */

export const COLORS = {
  // Brand — navyDark = CSS --navy (#0B1F36), navy = CSS --navy-med (#0F2B46)
  navyDark: '#0B1F36',
  navy: '#0F2B46',
  navyLight: '#163D5C',
  teal: '#1A9E8F',
  tealLight: '#E8F8F5',
  tealHover: '#168A7D',
  white: '#FFFFFF',

  // Score / severity — foreground + tint backgrounds for badges
  critical: '#C0392B',
  criticalBg: '#FDEDEC',
  warning: '#D4880F',
  warningBg: '#FEF5E7',
  pass: '#1E8449',
  passBg: '#EAFAF1',

  // Supporting grays (dashboard uses full ramp; PDF uses the original subset)
  gray50: '#FAFBFC',
  gray100: '#F8F9FA',
  gray200: '#DEE2E6',
  gray300: '#CED4DA',
  gray400: '#6C757D',
  gray600: '#495057',
  gray800: '#212529',

  textPrimary: '#1A1A2E',
  textSecondary: '#6C757D',

  bg: '#F4F5F7',
};

// PDF body font — Inter, embedded at generation time.
export const FONT = {
  family: "'Inter', sans-serif",
  weights: { light: 300, regular: 400, medium: 500, semibold: 600, bold: 700, extrabold: 800 },
};

// Dashboard body font — Inter, loaded from Google Fonts in the browser.
export const FONT_DASHBOARD = {
  family: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif",
  weights: { light: 300, regular: 400, medium: 500, semibold: 600, bold: 700, extrabold: 800 },
};

export const PAGE = {
  format: 'A4',
  widthMm: 210,
  heightMm: 297,
  marginTopMm: 0,
  marginBottomMm: 18,
  marginLeftMm: 18,
  marginRightMm: 18,
};

// Score -> color mapping (spec §1, 3-tier). PDF and dashboard must agree.
export function scoreColor(score) {
  if (score >= 80) return COLORS.pass;
  if (score >= 55) return COLORS.warning;
  return COLORS.critical;
}

// Severity -> color mapping (spec §11).
export function severityColor(severity) {
  switch (severity) {
    case 'CRITICAL': return COLORS.critical;
    case 'WARNING':  return COLORS.warning;
    case 'LOW':      return COLORS.pass;
    default:         return COLORS.gray400;
  }
}

// Verdict -> color mapping (spec §9).
export function verdictColor(verdict) {
  switch (verdict) {
    case 'APPROVED':             return COLORS.pass;
    case 'CORRECTIONS REQUIRED': return COLORS.warning;
    case 'REJECTED':             return COLORS.critical;
    default:                     return COLORS.gray400;
  }
}
